import { Settlement, SettlementMatrix, Contributor } from '../types/ui/Settlement';
import { formatDateTime } from '../utils/formatDate';
import { makeMoney, type Money } from '../utils/currency';
import { loadContributors } from './ContributorService';
import {
  fetchSettlements,
  fetchContributors,
  createSettlement as apiCreateSettlement,
  updateSettlement as apiUpdateSettlement,
} from './SettlementApiClient';

// ── Settlements ─────────────────────────────────────────────────────

export async function loadSettlements(): Promise<Settlement[]> {
  try {
    return await fetchSettlements();
  } catch (error) {
    console.error('Failed to load settlements:', error);
    return [];
  }
}

export async function createSettlement(data: Omit<Settlement, 'id' | 'created_at'>): Promise<Settlement> {
  try {
    return await apiCreateSettlement(data);
  } catch (error) {
    console.error('Failed to create settlement:', error);
    throw error;
  }
}

export async function updateSettlement(id: string, updates: Partial<Settlement>): Promise<Settlement | null> {
  try {
    return await apiUpdateSettlement(id, updates);
  } catch (error) {
    console.error(`Failed to update settlement ${id}:`, error);
    return null;
  }
}

// ── Settlement Reconciliation ────────────────────────────────────────

// Contributor.net_balance is a DB column that only ever gets written once,
// to 0, at creation (ContributorFormPage.tsx) — nothing anywhere updates it
// afterward, including when a settlement completes. Reading it directly, as
// this function and calculateWhoOwesWhom used to, meant every contributor
// was permanently balanced at exactly zero: calculateWhoOwesWhom's debtor/
// creditor filters (`net_balance < 0` / `> 0`) could never match anything,
// so the settlement page's "who owes whom" list was silently empty for
// every project with contributors, while the aggregate total above it
// (generateSettlementMatrix, a project-wide sum unrelated to this
// per-contributor field) looked correct — the two figures only looked
// consistent if you didn't check the breakdown against the total by hand.
//
// Computed live instead, same "never a stored figure that can drift"
// choice this app already made for budget_categories.actual_amount and
// the asoebi guest profile: what a contributor fronted (reimbursable
// items_paid_for) is owed back to them, reduced by completed settlements
// that already paid them (as the settlement's `to`) or that they already
// paid down (as the settlement's `from`).
export async function calculateContributorNetBalances(
  contributors: Contributor[],
  settlements: Settlement[],
): Promise<Map<string, number>> {
  const balances = new Map<string, number>();
  contributors.forEach(c => {
    const owedToThem = (c.items_paid_for ?? [])
      .filter(i => i.reimbursable)
      .reduce((sum, i) => sum + Number(i.amount), 0);
    const completed = settlements.filter(s => s.status === 'completed');
    const received = completed
      .filter(s => s.to_contributor_id === c.id)
      .reduce((sum, s) => sum + s.amount, 0);
    const paidOut = completed
      .filter(s => s.from_contributor_id === c.id)
      .reduce((sum, s) => sum + s.amount, 0);
    balances.set(c.id, owedToThem - received + paidOut);
  });
  return balances;
}

export async function calculateNetBalances(): Promise<Map<string, number>> {
  try {
    const [contributors, settlements] = await Promise.all([loadContributors(), loadSettlements()]);
    return calculateContributorNetBalances(contributors, settlements);
  } catch (error) {
    console.error('Failed to calculate net balances:', error);
    return new Map();
  }
}

export async function generateSettlementMatrix(): Promise<SettlementMatrix> {
  try {
    const contributors = await fetchContributors();
    const settlements = await fetchSettlements();

    const total_contributions = contributors.reduce((sum, c) => sum + c.total_contributed, 0);
    const total_expenses = contributors.reduce((sum, c) =>
      sum + (c.items_paid_for ?? []).filter(i => i.reimbursable).reduce((s, i) => s + Number(i.amount), 0),
      0
    );
    const settled_amount = settlements.filter(s => s.status === 'completed').reduce((sum, s) => sum + s.amount, 0);
    const net_balance = total_expenses - settled_amount;
    const unsettled = settlements.filter(s => s.status !== 'completed').reduce((sum, s) => sum + s.amount, 0);

    return {
      total_contributions,
      total_expenses,
      net_balance,
      settlements,
      contributors,
      unsettled_amount: unsettled,
      settlement_complete: net_balance <= 0.01,
    };
  } catch (error) {
    // Rethrown: the screen shows a load error with Retry. Returning an empty
    // matrix here made a failed load look like a project with no contributions.
    console.error('Failed to generate settlement matrix:', error);
    throw error;
  }
}

export async function calculateWhoOwesWhom(): Promise<Array<{
  from: Contributor;
  to: Contributor;
  amount: number;
}>> {
  try {
    const contributors = await fetchContributors();
    const settlements = await fetchSettlements();
    const liveBalances = await calculateContributorNetBalances(contributors, settlements);
    const balanceOf = (c: Contributor) => liveBalances.get(c.id) ?? 0;
    const result: Array<{ from: Contributor; to: Contributor; amount: number }> = [];

    const debtors = contributors
      .filter(c => balanceOf(c) < 0)
      .sort((a, b) => balanceOf(a) - balanceOf(b));
    const creditors = contributors
      .filter(c => balanceOf(c) > 0)
      .sort((a, b) => balanceOf(b) - balanceOf(a));

    const creditorBalances = new Map(creditors.map(c => [c.id, balanceOf(c)]));

    for (const debtor of debtors) {
      let remaining_debt = Math.abs(balanceOf(debtor));

      for (const creditor of creditors) {
        if (remaining_debt <= 0.01) break;
        const available = creditorBalances.get(creditor.id) ?? 0;
        if (available <= 0.01) continue;

        const settlement_amount = Math.min(remaining_debt, available);
        result.push({
          from: debtor,
          to: creditor,
          amount: settlement_amount,
        });

        remaining_debt -= settlement_amount;
        creditorBalances.set(creditor.id, available - settlement_amount);
      }
    }

    return result;
  } catch (error) {
    console.error('Failed to calculate who owes whom:', error);
    throw error;
  }
}

export async function generateSettlementReport(money: Money = makeMoney('')): Promise<string> {
  try {
    const matrix = await generateSettlementMatrix();
    const owings = await calculateWhoOwesWhom();

    let report = `PROJECT SETTLEMENT REPORT\n`;
    report += `Generated: ${formatDateTime(new Date())}\n`;
    report += `${'='.repeat(60)}\n\n`;

    report += `SUMMARY\n`;
    report += `Total Contributions: ${money.format(matrix.total_contributions)}\n`;
    report += `Total Expenses: ${money.format(matrix.total_expenses)}\n`;
    report += `Net Balance: ${money.format(matrix.net_balance)}\n`;
    report += `Unsettled Amounts: ${money.format(matrix.unsettled_amount)}\n`;
    report += `Settlement Status: ${matrix.settlement_complete ? 'COMPLETE' : 'PENDING'}\n\n`;

    report += `CONTRIBUTORS\n`;
    report += `${'-'.repeat(60)}\n`;
    matrix.contributors.forEach(c => {
      report += `${c.name} (${c.email})\n`;
      report += `  Contributed: ${money.format(c.total_contributed)}\n`;
      report += `  Net Balance: ${money.format(c.net_balance)}\n`;
      report += `  Status: ${c.reimbursement_status.toUpperCase()}\n\n`;
    });

    if (owings.length > 0) {
      report += `SETTLEMENT REQUIRED\n`;
      report += `${'-'.repeat(60)}\n`;
      owings.forEach(owing => {
        report += `${owing.from.name} → ${owing.to.name}: ${money.format(owing.amount)}\n`;
      });
    } else {
      report += `NO SETTLEMENTS REQUIRED - All balanced!\n`;
    }

    return report;
  } catch (error) {
    console.error('Failed to generate settlement report:', error);
    return 'Error generating report';
  }
}

export async function exportSettlementAsCSV(): Promise<string> {
  try {
    const matrix = await generateSettlementMatrix();
    const rows: string[] = [];

    rows.push('CONTRIBUTOR,CONTRIBUTED,NET_BALANCE,STATUS');
    matrix.contributors.forEach(c => {
      rows.push(`"${c.name}",${c.total_contributed},${c.net_balance},"${c.reimbursement_status}"`);
    });

    rows.push('');
    rows.push('SETTLEMENTS');
    rows.push('FROM,TO,AMOUNT,STATUS,METHOD');
    matrix.settlements.forEach(s => {
      rows.push(`"${s.from_contributor_name}","${s.to_contributor_name}",${s.amount},"${s.status}","${s.method || 'pending'}"`);
    });

    return rows.join('\n');
  } catch (error) {
    console.error('Failed to export settlement as CSV:', error);
    return '';
  }
}
